import { component } from 'tsdi';
import { makeAutoObservable } from 'mobx';
import * as React from 'react';
import ReactGA_UA from 'react-ga';
import ReactGA_4 from 'react-ga4';
import { injectTSDI } from '../tsdi';
import { featureEnabled } from '../features';
import { CookieNoticeStore } from '../../components/cookie-notice/store';
import { isProd } from '../../core/api';

export const UtmParams = [
    'utm_id',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_creative_format',
    'utm_source_platform',
    'utm_marketing_tactic'
];

export type UtmParamsType = keyof typeof UtmParams[number];

// https://developers.google.com/analytics/devguides/collection/gtagjs/user-opt-out
const gaDisableKey = (accountId: string) => `ga-disable-${accountId}`;

const GoogleAnalyticsId = isProd() ? 'G-TWR2N9W93K' : 'G-CLQGZ4YMX8';

const getGA = (
    accountId: string
): typeof ReactGA_UA | typeof ReactGA_4 | undefined => {
    if (accountId.startsWith('UA-')) {
        return ReactGA_UA;
    }
    if (accountId.startsWith('G-')) {
        return ReactGA_4;
    }
    console.warn(`Unrecognized Google Analytics accountId: ${accountId}`);
    return undefined;
};

@component
export class GoogleAnalytics {
    constructor() {
        makeAutoObservable(this);
    }

    private cookieNoticeStore = injectTSDI(CookieNoticeStore);

    private initializedAccounts: string[] = [];

    public enableAccount = async (accountId: string) => {
        const ga = getGA(accountId);

        if (!ga) {
            return;
        }

        if (this.initializedAccounts.includes(accountId)) {
            return;
        }

        this.initializedAccounts.push(accountId);

        if (ga === ReactGA_UA) {
            ReactGA_UA.initialize(accountId);
            ReactGA_UA.pageview(window.location.href, [window.document.title]);

            ReactGA_4.initialize(GoogleAnalyticsId);
            ReactGA_4.send('pageview');
        } else {
            ga.initialize([
                {
                    trackingId: accountId
                },
                {
                    trackingId: GoogleAnalyticsId
                }
            ]);

            ReactGA_4.send('pageview');
        }
    };

    public disableAccount = (accountId: string) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any)[gaDisableKey(accountId)] = true;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public trackEvent = (option: any) => {
        if (!this.cookieNoticeStore.marketingEventsEnabled) {
            return;
        }

        if (!this.initializedAccounts.length) {
            return;
        }

        const allGAs = this.initializedAccounts.map(getGA);

        if (allGAs.includes(ReactGA_UA)) {
            allGAs.map((GA, i) => {
                if (this.initializedAccounts[i] !== GoogleAnalyticsId) {
                    ReactGA_UA.ga('config', GA);
                    ReactGA_UA.event(option);
                }
            });

            ReactGA_4.gtag('config', ReactGA_4);
            ReactGA_4.event(option);
        }
        if (allGAs.includes(ReactGA_4)) {
            allGAs.map((GA, i) => {
                if (this.initializedAccounts[i] !== GoogleAnalyticsId) {
                    ReactGA_4.gtag('config', GA);
                    ReactGA_4.event(option);
                }
            });
        }

        if (featureEnabled('IS_DEV')) {
            console.info(
                `Google Analytics: tracked event ${JSON.stringify(
                    'option',
                    option
                )}`
            );
        }
    };
}

export const useScopedGoogleAnalytics = (accountId?: string) => {
    const googleAnalyticsStore = injectTSDI(GoogleAnalytics);

    React.useEffect(() => {
        if (accountId) {
            googleAnalyticsStore.enableAccount(accountId);
        }

        return () => {
            if (accountId) {
                googleAnalyticsStore.disableAccount(accountId);
            }
        };
    }, [accountId]);
};
